import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getAppTheme } from '../theme';

const ThemeModeContext = createContext({
  mode: 'dark',
  toggleColorMode: () => {},
  setColorMode: () => {},
});

export const ThemeModeProvider = ({ children }) => {
  // Read initial mode from localStorage, defaulting to 'dark'
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('gpn_theme_mode');
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    return 'dark';
  });

  useEffect(() => {
    localStorage.setItem('gpn_theme_mode', mode);
    document.documentElement.setAttribute('data-theme', mode);
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(`${mode}-mode`);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'dark' ? 'light' : 'dark'));
  };

  const setColorMode = (newMode) => {
    if (newMode === 'light' || newMode === 'dark') {
      setMode(newMode);
    }
  };

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleColorMode,
      setColorMode,
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);

export default ThemeModeContext;
